/**
 * "Priority for the provider" from Manifest Review (ruling, Isaiah Cotto, 2026-10-27): a VA (or a PA manager) flags a
 * resident at the building as a priority before the visit, with an optional reason of at most 100 characters, and the
 * provider sees it on the facility rounds table ("set by the office · <first name>"). The same mark the provider's
 * star sets (provider_priority_events, 20261027d): per building and resident, not per day; either side clears it.
 * docs/provider/FACILITY_ROUNDS.md. Server: routes/manifestPriority.js (MANIFEST_REVIEW at the building).
 *
 * OfficePriorityProvider reads the building's current priorities once for the review's residents;
 * OfficePriorityToggle is the control on one resident line. Before 20261027d (or on an older server) nothing renders.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { apiRequest } from '../../services/api';
import { Button } from '../../components/new-components/ui/button';
import './office-priority.css';

export const OFFICE_REASON_MAX = 100;

export interface OfficePriorityView {
  reason: string | null;
  kind?: 'PROVIDER' | 'OFFICE';
  by: { first_name: string; mine: boolean };
  at: string | null;
}

const BASE = '/manifest-intake';
export const officePriorityApi = {
  list: (facilityId: string) =>
    apiRequest<{ ready: boolean; items: Record<string, OfficePriorityView> }>('GET', `${BASE}/facilities/${facilityId}/priorities`),
  set: (facilityId: string, patientId: string, priority: boolean, reason?: string | null) =>
    apiRequest<{ patient_id: string; changed: boolean; priority: OfficePriorityView | null }>(
      'POST', `${BASE}/facilities/${facilityId}/residents/${patientId}/priority`,
      priority ? { priority, reason: reason && reason.trim() ? reason.trim() : null } : { priority },
    ),
};

interface Ctx {
  facilityId: string;
  items: Record<string, OfficePriorityView>;
  busy: Set<string>;
  error: string;
  set: (patientId: string, priority: boolean, reason?: string | null) => Promise<boolean>;
}
const OfficePriorityContext = createContext<Ctx | null>(null);

const isList = (d: unknown): d is { ready: boolean; items: Record<string, OfficePriorityView> } => {
  const x = d as { ready?: unknown; items?: unknown } | null;
  return Boolean(x && typeof x === 'object' && x.ready === true && x.items && typeof x.items === 'object');
};

export function OfficePriorityProvider({ facilityId, children }: { facilityId: string; children: React.ReactNode }) {
  const [items, setItems] = useState<Record<string, OfficePriorityView> | null>(null);
  const [busy, setBusy] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    setItems(null);
    officePriorityApi.list(facilityId).then(
      (r) => { if (live) setItems(r && r.success && isList(r.data) ? r.data.items : null); },
      () => { if (live) setItems(null); },
    );
    return () => { live = false; };
  }, [facilityId]);
  const set = useCallback(async (patientId: string, priority: boolean, reason?: string | null) => {
    setError('');
    setBusy((b) => new Set(b).add(patientId));
    try {
      const r = await officePriorityApi.set(facilityId, patientId, priority, reason);
      if (r && r.success && r.data) {
        const view = r.data.priority;
        setItems((cur) => {
          const next = { ...(cur || {}) };
          if (view) next[patientId] = view; else delete next[patientId];
          return next;
        });
        return true;
      }
      setError((r && ((r as { message?: string }).message || r.error)) || 'eMerge did not confirm that. Nothing changed; try again.');
      return false;
    } catch {
      setError('eMerge did not confirm that. Nothing changed; try again.');
      return false;
    } finally {
      setBusy((b) => { const n = new Set(b); n.delete(patientId); return n; });
    }
  }, [facilityId]);
  // Not switched on (no table, an older server, a refusal): no control, the review is unchanged.
  if (!items) return <>{children}</>;
  return (
    <OfficePriorityContext.Provider value={{ facilityId, items, busy, error, set }}>
      {error && <p role="alert" className="mf-prio-error">{error}</p>}
      {children}
    </OfficePriorityContext.Provider>
  );
}

const whoSet = (p: OfficePriorityView) =>
  (p.by.mine ? 'you' : p.kind === 'OFFICE' ? `${p.by.first_name} (office)` : `${p.by.first_name} (provider)`);

/** The control on one resident line of the review. */
export function OfficePriorityToggle({ patientId, name }: { patientId: string | null; name: string }) {
  const ctx = useContext(OfficePriorityContext);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('');
  if (!ctx || !patientId) return null;
  const p = ctx.items[patientId] || null;
  const busy = ctx.busy.has(patientId);
  return (
    <div className="mf-prio">
      <button
        type="button"
        className={`mf-prio-pill${p ? ' is-on' : ''}`}
        aria-pressed={p ? 'true' : 'false'}
        aria-expanded={editing}
        aria-label={p
          ? `Priority for the provider: ${name}${p.reason ? `, ${p.reason}` : ''}, set by ${whoSet(p)}. Change or clear.`
          : `Mark ${name} a priority for the provider`}
        disabled={busy}
        onClick={() => { setReason(p && p.reason ? p.reason : ''); setEditing((e) => !e); }}
      >
        <Star aria-hidden="true" className={p ? 'mf-prio-star' : undefined} /> Priority for the provider
        {p && p.reason ? <small> · {p.reason}</small> : null}
        {p ? <small> · set by {whoSet(p)}</small> : null}
      </button>
      {editing && (
        <form
          className="mf-prio-editor"
          aria-label={`Priority for the provider: ${name}`}
          onSubmit={async (ev) => {
            ev.preventDefault();
            if (await ctx.set(patientId, true, reason)) setEditing(false);
          }}
        >
          <label htmlFor={`mf-prio-${patientId}`}>Reason for the provider (optional)</label>
          <input
            id={`mf-prio-${patientId}`}
            value={reason}
            maxLength={OFFICE_REASON_MAX}
            placeholder="e.g. family meeting, hearing aid broken"
            onChange={(ev) => setReason(ev.target.value)}
          />
          <small className="mf-quiet">{reason.length}/{OFFICE_REASON_MAX} · shown on the provider&rsquo;s rounds table for this building until someone clears it</small>
          <div className="mf-prio-actions">
            <Button type="submit" disabled={busy}>{p ? 'Save' : 'Mark priority'}</Button>
            {p && (
              <Button type="button" variant="outline" disabled={busy} onClick={async () => { if (await ctx.set(patientId, false)) setEditing(false); }}>
                Clear priority
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );
}
